import NotificationManager from "../../common/notification/NotificationManager";
import { API_CALL_NOTIFICATION } from "../constants/notification";

/**
 * Handles any error returned from getAccessToken. The numbered errors are typically user actions
 * that don't require fallback auth. The text shown for each error indicates next steps
 * you should take. For default (all other errors), the sample returns true
 * so that the caller is informed to use fallback auth.
 *
 * @param {*} err The error to process.
 * @returns true if SSO error could not be handled, and fallback auth is required; otherwise, false.
 */
export function handleSSOErrors(err: { code: number; message: string }) {
  const showMessage = (message: string) => {
    NotificationManager.addNotification({
      title: "Error",
      message: message,
      key: API_CALL_NOTIFICATION,
      type: "error",
      closable: true,
      removeOnClose: true,
      isTemporary: false,
    });
  };

  let fallbackRequired = false;
  switch (err.code) {
    case 13001:
      // No one is signed into Office. If the add-in cannot be effectively used when no one
      // is logged into Office, then the first call of getAccessToken should pass the
      // `allowSignInPrompt: true` option. Since this sample does that, you should not see
      // this error.
      showMessage("No one is signed into Office. If you need this functionality please login in.");
      break;
    case 13002:
      // The user aborted the consent prompt. If the add-in cannot be effectively used when consent
      // has not been granted, then the first call of getAccessToken should pass the `allowConsentPrompt: true` option.
      showMessage(
        "You can use many of the add-ins functions even though you have not granted consent. If you want to grant consent, press the Get OneDrive File Names button again."
      );
      break;
    case 13006:
      // Only seen in Office on the web.
      showMessage(
        "Office on the web is experiencing a problem. Please sign out of Office, close the browser, and then start again."
      );
      break;
    case 13008:
      // Only seen in Office on the web.
      showMessage("Office is still working on the last operation. When it completes, try this operation again.");
      break;
    case 13010:
      // Only seen in Office on the web.
      showMessage("Follow the instructions to change your browser's zone configuration.");
      break;
    case 13013:
      // API Office.auth.getAccessToken has been throttled to slow the call frequency.
      // eslint-disable-next-line no-case-declarations
      const customMessage = `There was an unexpected error on Microsoft api call, please try again. (${err.message})`;
      // showMessage(customMessage);
      // in this case we want to break the flow a stop the api call
      throw new Error(customMessage);
    default:
      // For all other errors, including 13000, 13003, 13005, 13007, 13012, and 50001, fall back
      // to non-SSO sign-in.
      fallbackRequired = true;
      break;
  }
  return fallbackRequired;
}
